import { useRef, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { dataGridPremiumColumns } from '../../../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  CheckboxUI,
  InputUI,
} from '../../../Interface';

const EditToolbar = (props) => {
  const { disabled, push, defConformityComponent } = props;

  const handleClick = async () => {
    defConformityComponent.id = uuidv4();
    defConformityComponent.isNew = true;
    await push(defConformityComponent);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ConformityComponent = ({ defConformityComponent, viewField }) => {
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.conformityComponent.findIndex((rc) => rc.id === id);
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'stringAttr1',
          headerName: 'LV',
          type: 'string',
          editable: true,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`conformityComponent.${valueIndex}.stringAttr1`}
                as={InputUI}
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.conformityComponent?.[valueIndex]?.stringAttr1} />}
                onChange={(e) => {
                  setFieldValue(`conformityComponent.${valueIndex}.stringAttr1`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`conformityComponent.${valueIndex}.stringAttr1`)}
              />
            );
          },
        },
        {
          field: 'stringAttr2',
          headerName: 'Minőség',
          type: 'string',
          editable: true,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`conformityComponent.${valueIndex}.stringAttr2`}
                as={InputUI}
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.conformityComponent?.[valueIndex]?.stringAttr2} />}
                onChange={(e) => {
                  setFieldValue(`conformityComponent.${valueIndex}.stringAttr2`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`conformityComponent.${valueIndex}.stringAttr2`)}
              />
            );
          },
        },
        {
          field: 'stringAttr3',
          headerName: 'Adagszám',
          type: 'string',
          editable: true,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`conformityComponent.${valueIndex}.stringAttr3`}
                as={InputUI}
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.conformityComponent?.[valueIndex]?.stringAttr3} />}
                onChange={(e) => {
                  setFieldValue(`conformityComponent.${valueIndex}.stringAttr3`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`conformityComponent.${valueIndex}.stringAttr3`)}
              />
            );
          },
        },
        {
          field: 'boolAttr1',
          headerName: 'Hozaganyag',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`conformityComponent.${valueIndex}.boolAttr1`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.conformityComponent?.[valueIndex]?.boolAttr1} />}
                checked={values.conformityComponent?.[valueIndex]?.boolAttr1 ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`conformityComponent.${valueIndex}.boolAttr1`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },
        {
          field: 'boolAttr2',
          headerName: 'Egyéb',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`conformityComponent.${valueIndex}.boolAttr2`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.conformityComponent?.[valueIndex]?.boolAttr2} />}
                checked={values.conformityComponent?.[valueIndex]?.boolAttr2 ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`conformityComponent.${valueIndex}.boolAttr2`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },

        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={viewField}
              />,
            ];
          },
        },
      ]),
    [values.conformityComponent, errors, setFieldValue, validateField, viewField]
  );

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="conformityComponent">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.ConformityComponent}
                  height={480}
                  columns={columns(remove)}
                  rows={values?.conformityComponent ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled: viewField,
                      push,
                      defConformityComponent,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConformityComponent;
